import { useState } from 'react';
import { alpha } from '@mui/material/styles';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import LinkOffRounded from '@mui/icons-material/LinkOffRounded';
import StorefrontRounded from '@mui/icons-material/StorefrontRounded';
import StarRounded from '@mui/icons-material/StarRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import { disconnectStore, setMainStore, updateStoreNickname } from '../services/shopifyApi';
import storeLabel from '../utils/storeLabel';

const statusColors = {
  active: '#00D9A6',
  pending: '#FF9800',
  disconnected: '#EF4444',
};

const StoresTable = ({ stores, onStoreDisconnected, readOnly = false }) => {
  const [disconnecting, setDisconnecting] = useState(null);
  const [settingMain, setSettingMain] = useState(null);
  const [nickDialog, setNickDialog] = useState({ open: false, store: null });
  const [nickValue, setNickValue] = useState('');
  const [savingNick, setSavingNick] = useState(false);

  const openNickDialog = (store) => {
    setNickDialog({ open: true, store });
    setNickValue(store.nickname || '');
  };

  const handleSaveNick = async () => {
    if (!nickDialog.store) return;
    setSavingNick(true);
    try {
      await updateStoreNickname(nickDialog.store.id, nickValue.trim());
      setNickDialog({ open: false, store: null });
      onStoreDisconnected(nickDialog.store.id); // triggers a stores refresh
    } catch (err) {
      console.error('Failed to save nickname:', err);
    } finally {
      setSavingNick(false);
    }
  };

  const handleDisconnect = async (storeId) => {
    if (!window.confirm('Are you sure you want to disconnect this store?')) {
      return;
    }

    setDisconnecting(storeId);
    try {
      await disconnectStore(storeId);
      onStoreDisconnected(storeId);
    } catch (err) {
      console.error('Failed to disconnect store:', err);
    } finally {
      setDisconnecting(null);
    }
  };

  const handleSetMain = async (storeId) => {
    setSettingMain(storeId);
    try {
      await setMainStore(storeId);
      onStoreDisconnected(storeId); // triggers refresh
    } catch (err) {
      console.error('Failed to set main store:', err);
    } finally {
      setSettingMain(null);
    }
  };

  if (stores.length === 0) {
    return (
      <Card>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <StorefrontRounded sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography variant="h6" color="text.secondary">
            No stores connected
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {readOnly
              ? 'No stores have been connected by an admin yet.'
              : 'Click "Add Store" to connect your first Shopify store'}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Store</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Role</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Connected</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {stores.map((store) => (
              <TableRow key={store.id} sx={{ '&:last-child td': { border: 0 } }}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <StorefrontRounded sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {storeLabel(store)}
                      </Typography>
                      {store.nickname && store.nickname.trim() && (
                        <Typography variant="caption" color="text.secondary">
                          {store.shop_domain}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={store.status.charAt(0).toUpperCase() + store.status.slice(1)}
                    size="small"
                    sx={{
                      bgcolor: alpha(statusColors[store.status] || '#888', 0.12),
                      color: statusColors[store.status] || '#888',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                    }}
                  />
                </TableCell>
                <TableCell>
                  {store.is_main ? (
                    <Chip
                      icon={<StarRounded sx={{ fontSize: 16 }} />}
                      label="Main"
                      size="small"
                      sx={{
                        bgcolor: alpha('#6C63FF', 0.1),
                        color: '#6C63FF',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                      }}
                    />
                  ) : store.status === 'active' && !readOnly ? (
                    <Button
                      size="small"
                      onClick={() => handleSetMain(store.id)}
                      disabled={settingMain === store.id}
                      sx={{ fontSize: '0.75rem', color: 'text.secondary' }}
                    >
                      {settingMain === store.id ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : (
                        'Set as Main'
                      )}
                    </Button>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      --
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {store.installed_at
                      ? new Date(store.installed_at).toLocaleDateString()
                      : '--'}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  {!readOnly ? (
                    <Box sx={{ display: 'inline-flex', gap: 0.5 }}>
                      <Tooltip title="Rename (nickname)">
                        <IconButton
                          size="small"
                          onClick={() => openNickDialog(store)}
                          sx={{ color: 'primary.main' }}
                        >
                          <EditRounded fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {store.status === 'active' && (
                        <Tooltip title="Disconnect store">
                          <IconButton
                            size="small"
                            onClick={() => handleDisconnect(store.id)}
                            disabled={disconnecting === store.id}
                            sx={{ color: 'error.main' }}
                          >
                            {disconnecting === store.id ? (
                              <CircularProgress size={18} color="inherit" />
                            ) : (
                              <LinkOffRounded fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      --
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={nickDialog.open} onClose={() => setNickDialog({ open: false, store: null })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Rename store</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {nickDialog.store?.shop_domain}
          </Typography>
          <TextField
            autoFocus
            label="Nickname"
            placeholder="e.g. Prince Seating"
            value={nickValue}
            onChange={(e) => setNickValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSaveNick(); }}
            fullWidth
            helperText="Shown across the dashboard. Leave blank to use the store domain."
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setNickDialog({ open: false, store: null })}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveNick} disabled={savingNick}>
            {savingNick ? <CircularProgress size={20} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default StoresTable;
